import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  UserPicker,
  type UserPickerOption,
} from '@/components/ui/user-picker';
import { useUserSearch } from '@/hooks/useUserSearch';

interface AddMemberFormProps {
  readonly busy: boolean;
  readonly isAlreadyOnTeam: (userGuid: string) => boolean;
  /**
   * Appends the picked user to the editor's working copy (no server call here —
   * the diff is committed on Save).
   */
  readonly onAdd: (input: {
    readonly userId: string;
    readonly displayName: string;
    readonly role: string;
  }) => void;
  /** Bumped by the parent after an add so the picker + role field clear together. */
  readonly resetKey: number;
}

/** Picker + role + confirm button for adding one care-team member at a time. */
export function AddMemberForm({
  busy,
  isAlreadyOnTeam,
  onAdd,
  resetKey,
}: AddMemberFormProps): ReactElement {
  const { t } = useTranslation();
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickedUser, setPickedUser] = useState<UserPickerOption | null>(null);
  const [newRole, setNewRole] = useState('');
  const pickerSearch = useUserSearch(pickerQuery);

  // Parent bumps `resetKey` after a successful add so the picker + role field
  // clear without threading a controlled reset callback through props.
  useEffect(() => {
    setPickedUser(null);
    setNewRole('');
  }, [resetKey]);

  const canAdd =
    pickedUser !== null &&
    newRole.trim().length > 0 &&
    !isAlreadyOnTeam(pickedUser.guid);

  const handleAdd = (): void => {
    if (!canAdd) return;
    onAdd({ userId: pickedUser.guid, displayName: pickedUser.fullName, role: newRole.trim() });
  };

  return (
    <div className="mt-2 space-y-2">
      <UserPicker
        value={pickedUser}
        onChange={(option) => {
          setPickedUser(option);
          if (option !== null) setPickerQuery('');
        }}
        options={pickerSearch.results}
        isLoading={pickerSearch.isLoading}
        ariaLabel={t('patient.careTeam.addMemberAriaLabel')}
        placeholder={t('patient.careTeam.addMemberPlaceholder')}
        query={pickerQuery}
        onQueryChange={setPickerQuery}
      />
      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={newRole}
          placeholder={t('patient.careTeam.addMemberRolePlaceholder')}
          aria-label={t('patient.careTeam.addMemberRoleAriaLabel')}
          onChange={(event) => {
            setNewRole(event.target.value);
          }}
          className="h-9 flex-1 text-sm"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleAdd}
          disabled={!canAdd || busy}
        >
          {t('patient.careTeam.addMemberConfirm')}
        </Button>
      </div>
    </div>
  );
}
